import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import { Theme, useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { colors } from "@mui/material";
import { FilterPanels } from "./types";
import { SearchBar } from "./SearchBar";
import { FilterButton, renderButtonLabel } from "./FilterButton";
import { v4 as uuid } from "uuid";
import { useAppStore } from "@/global/store";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import React from "react";
import { createPortal } from "react-dom";
import { MobileFilterDrawerNav } from "./game_filters_helpers";
import { filterSelectorTitles } from "./useFilterPanels";
import { FiPlus, FiMinus } from "react-icons/fi";
import { isFiltersApplied } from "./utils.gameFilters";
import { ClearFilterButton } from "./ClearFilterButton";

interface Props {
  selectedFilter: string;
  onClickFilterButton: (a: string) => void;
  onClearFilters?: () => void;
  filterPanels: FilterPanels;
  isClearFiltersButtonVisible: boolean;
}

export function MobileFilters({
  selectedFilter,
  onClickFilterButton,
  filterPanels,
  onClearFilters,
  isClearFiltersButtonVisible,
}: Props) {
  const teamConfig = useTeamConfig();
  const theme = useTheme();
  const styles = useStyles(theme, { selectedFilter });
  const gameFilters = useAppStore((s) => s.data.gameFilters);

  return (
    <>
      <Box width="100%">
        <SearchBar
          onMouseEnter={() => {}}
          onMouseLeave={() => {}}
          isBottomPanelOpen={!!selectedFilter}
          isHidden={filterPanels.every((itm) => itm.isHidden)}
          renderButtons={() => {
            return (
              <Stack spacing={1} direction={"row"} ml={0} pr={2}>
                {filterPanels.map((itm) => {
                  return (
                    <FilterButton
                      key={uuid()}
                      name={itm.name}
                      label={renderButtonLabel(
                        itm.name,
                        gameFilters,
                        teamConfig
                      )}
                      isSelected={itm.name == selectedFilter}
                      onClick={onClickFilterButton}
                      isDisabled={itm.isDisabled}
                      isHidden={itm.isHidden}
                    />
                  );
                })}
              </Stack>
            );
          }}
        />
      </Box>

      <Box sx={styles.fullScreenWrapper}>
        <Stack width={"100%"} height={"100%"}>
          <Stack
            py={2}
            borderBottom={`1px solid ${theme.palette.divider}`}
            width={"100%"}
            zIndex={999}
            bgcolor={theme.palette.common.white}
          >
            <MobileFilterDrawerNav
              onClickCloseIcon={() => onClickFilterButton("")}
            />
          </Stack>

          <Box flexGrow={1} width={"100%"} sx={{ overflowY: "auto" }}>
            {filterPanels
              .filter((itm) => !itm.isHidden)
              .map((itm, idx) => {
                const isAccordionOpen = selectedFilter == itm.name;

                return (
                  <Accordion
                    key={idx}
                    sx={styles.accordion}
                    expanded={isAccordionOpen}
                  >
                    <AccordionSummary
                      expandIcon={
                        <IconButton
                          disabled={isAccordionOpen || itm.isDisabled}
                          sx={{
                            color: colors.grey[800],
                            backgroundColor: `${colors.grey[100]} !important`,
                            "&:active": { backgroundColor: colors.grey[200] },
                          }}
                        >
                          {isAccordionOpen ? <FiMinus /> : <FiPlus />}
                        </IconButton>
                      }
                      onClick={() => onClickFilterButton(itm.name)}
                      disabled={itm.isDisabled}
                    >
                      <Typography
                        variant="subtitle1"
                        fontWeight={"600"}
                        color="text.primary"
                      >
                        {
                          filterSelectorTitles[
                            itm.name as keyof typeof filterSelectorTitles
                          ]
                        }
                      </Typography>
                    </AccordionSummary>

                    <AccordionDetails sx={styles.accordionDetails}>
                      <Box pt={2}>{itm.component}</Box>
                      {itm.footerComponent && (
                        <Box mt={4}>{itm.footerComponent}</Box>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })}

            <Box height={"160px"} />
          </Box>
        </Stack>
      </Box>

      {!!selectedFilter
        ? createPortal(
            <Box
              position={"fixed"}
              zIndex={999}
              bottom={0}
              width={"100%"}
              p={2}
              borderTop={`1px solid ${theme.palette.divider}`}
              bgcolor={theme.palette.common.white}
            >
              <Stack spacing={1} width={"100%"}>
                {isClearFiltersButtonVisible ? (
                  <ClearFilterButton
                    onClick={() => {
                      // clearing the filters
                      onClearFilters?.();
                      // closing the filter drawer
                      onClickFilterButton("");
                    }}
                    chipProps={{ size: "large" }}
                  />
                ) : null}

                <Button
                  size="large"
                  fullWidth
                  variant="contained"
                  onClick={() => {
                    onClickFilterButton("");
                  }}
                >
                  Apply
                </Button>
              </Stack>
            </Box>,
            document.body
          )
        : null}
    </>
  );
}

function useStyles(theme: Theme, { selectedFilter }: Partial<Props>) {
  return {
    fullScreenWrapper: {
      width: "100%",
      height: "100vh",
      backgroundColor: theme.palette.background.default,
      zIndex: 9999,
      position: "fixed",
      top: 0,
      left: 0,
      overflowX: "hidden",
      pointerEvents: !!selectedFilter ? "all" : "none",
      opacity: !!selectedFilter ? 1 : 0,
      // transform: !!selectedFilter ? "scaleY(1)" : "scaleY(0)",
      transformOrigin: "top",
      transition: "transform 0.2s ease",
    },

    accordion: {
      borderRadius: "0 !important",
      boxShadow: "none",
      borderBottom: `1px solid ${theme.palette.divider}`,
      paddingTop: 1,
      paddingBottom: 1,

      "& .MuiAccordionSummary-root": {
        paddingLeft: 2,
        paddingRight: 2,
      },

      "& .MuiAccordionSummary-expandIconWrapper svg": {
        width: "1.5rem",
        height: "1.5rem",
      },
      "&.MuiAccordion-root.Mui-expanded": {
        margin: "0px !important",
      },
    },

    accordionDetails: {
      borderTop: `1px solid ${theme.palette.divider}`,
    },
  };
}
